import * as React from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"
import { Download } from "lucide-react"

import { TableDisplayContext } from "@/contexts/TableDisplayContext"

interface TableCsvDownloadContentProps {
  onClick: () => void | Promise<void>
  disabled?: boolean
  rowCount: number
  columns: string[]
  filters?: string[]
  scope?: string
}

interface TableCsvDownloadDialogProps extends TableCsvDownloadContentProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface TableCsvDownloadButtonProps extends TableCsvDownloadContentProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const DEFAULT_SCOPE =
  "Includes the rows currently loaded in this table. Rows on other pages or not yet loaded are not included."

/**
 * Confirms the current filtered export before creating a local download.
 * Rendered on its own where the export is triggered from somewhere other than
 * the table's toolbar, such as a resource's overflow menu.
 */
export function TableCsvDownloadDialog({
  open,
  onOpenChange,
  onClick,
  disabled,
  rowCount,
  columns,
  filters,
  scope = DEFAULT_SCOPE,
}: TableCsvDownloadDialogProps) {
  const context = React.useContext(TableDisplayContext)
  const [isDownloading, setIsDownloading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const summaries = filters ?? context?.exportFilters ?? []

  React.useEffect(() => {
    if (open) {
      setError(null)
    }
  }, [open])

  const handleDownload = async () => {
    setError(null)
    setIsDownloading(true)
    try {
      await onClick()
      onOpenChange(false)
    } catch {
      setError("The CSV could not be downloaded. Please try again.")
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isDownloading) {
          onOpenChange(next)
        }
      }}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Download CSV</DialogTitle>
          <DialogDescription>
            You’re downloading your current filtered view.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="font-medium">
            {rowCount.toLocaleString()} {rowCount === 1 ? "row" : "rows"} ·{" "}
            {columns.length} {columns.length === 1 ? "column" : "columns"}
          </p>
          <p className="text-sm text-coval-text-muted">{scope}</p>
          <div className="space-y-2">
            <p className="text-sm font-medium">Applied filters</p>
            {summaries.length ? (
              <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-coval-text-muted">
                {summaries.map((summary, index) => (
                  <li key={`${index}:${summary}`} className="break-words">
                    {summary}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-coval-text-muted">
                No additional filters or search.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">Exported columns</p>
            <p className="max-h-32 overflow-y-auto text-sm text-coval-text-muted">
              {columns.join(", ")}
            </p>
          </div>
          {error && (
            <p role="alert" className="text-sm text-coval-error">
              {error}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            disabled={isDownloading}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleDownload()}
            loading={isDownloading}
            disabled={disabled || rowCount === 0 || columns.length === 0}
          >
            <Download aria-hidden />
            Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The table toolbar's download control on its own, for callers that own the
 * confirmation dialog themselves.
 */
export function TableCsvDownloadTrigger({
  onClick,
  disabled,
}: {
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <Button
      type="button"
      variant="secondary"
      icon
      tooltip="Download CSV"
      aria-label="Download CSV"
      disabled={disabled}
      onClick={onClick}
    >
      <Download aria-hidden />
    </Button>
  )
}

/** The table toolbar's download control, with its confirmation dialog. */
export function TableCsvDownloadButton({
  open: controlledOpen,
  onOpenChange,
  disabled,
  rowCount,
  columns,
  ...contentProps
}: TableCsvDownloadButtonProps) {
  const [localOpen, setLocalOpen] = React.useState(false)
  const open = controlledOpen ?? localOpen
  const setOpen = (value: boolean) => {
    setLocalOpen(value)
    onOpenChange?.(value)
  }

  return (
    <>
      <TableCsvDownloadTrigger
        disabled={disabled || rowCount === 0 || columns.length === 0}
        onClick={() => setOpen(true)}
      />
      <TableCsvDownloadDialog
        open={open}
        onOpenChange={setOpen}
        disabled={disabled}
        rowCount={rowCount}
        columns={columns}
        {...contentProps}
      />
    </>
  )
}
